import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

const ICONS = { meta_ads: "🎯", google_ads: "🔎", seo: "📈", creative: "✨" };

export default function AdminServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error("Yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    const slug = window.prompt("Yeni hizmet slug (ör: email-marketing):", "");
    if (!slug) return;
    const trimmed = slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-");
    setCreating(true);
    try {
      await api.post("/admin/services", {
        slug: trimmed,
        icon: "Sparkles",
        order: 99,
        review_tags: [],
        pricing: [],
        content: { tr: { hero_eyebrow: "", hero_title_pre: "", hero_title_accent: "", hero_subtitle: "" } },
        translated_langs: ["tr"],
      });
      toast.success("Oluşturuldu — TR içeriği doldur, AI çeviriyi sonra çalıştır.");
      window.location.href = `/admin/services/${trimmed}/edit`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Oluşturulamadı"); }
    finally { setCreating(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`"${slug}" hizmetini silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/admin/services/${slug}`);
      toast.success("Silindi");
      refresh();
    } catch (e) { toast.error("Silinemedi"); }
  };

  return (
    <div data-testid="services-admin-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold">Hizmetler</h1>
          <p className="mt-1.5 text-sm text-ink/55">Servis landing sayfaları — 6 dilde içerik, fiyat paketleri, FAQ.</p>
        </div>
        <button type="button" onClick={create} disabled={creating}
          className="btn-gold !text-xs !py-2.5 inline-flex items-center gap-2"
          data-testid="services-create-btn">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Yeni hizmet
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-ink/50"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-mist p-10 text-center text-ink/55">
          Henüz hizmet yok.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((s) => {
            const tr = (s.content && s.content.tr) || {};
            const titlePieces = [tr.hero_title_pre, tr.hero_title_accent].filter(Boolean).join(" ");
            const langs = Array.isArray(s.translated_langs) ? s.translated_langs : [];
            return (
              <div key={s.slug} data-testid={`service-card-${s.slug}`}
                className="rounded-2xl bg-white border border-mist p-5 hover:border-brand-pink/40 transition-colors">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{ICONS[s.slug.replace(/-/g, "_")] || "✨"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-[0.16em] text-ink/45">{s.slug}</p>
                    <h3 className="font-display text-lg font-bold text-ink truncate mt-0.5">
                      {titlePieces || s.slug}
                    </h3>
                    {tr.hero_subtitle && <p className="text-xs text-ink/55 line-clamp-2 mt-1.5">{tr.hero_subtitle}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                  {["tr", "en", "es", "de", "fr", "ar"].map((l) => (
                    <span key={l} className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded
                      ${langs.includes(l) ? "bg-brand-pink/10 text-brand-pink-deep" : "bg-mist text-ink/40"}`}>
                      {l}
                    </span>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <Link
                    to={`/admin/services/${s.slug}/edit`}
                    data-testid={`service-edit-${s.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-pink hover:text-brand-pink-deep">
                    <Pencil className="h-3.5 w-3.5" /> Düzenle
                  </Link>
                  <span className="text-ink/20">·</span>
                  <button type="button" onClick={() => remove(s.slug)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/40 hover:text-destructive"
                    data-testid={`service-delete-${s.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
